import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { useSnackbar } from '../contexts/SnackbarContext';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  TextareaAutosize,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Download,
  PlayCircleOutline,
  EventAvailable,
  Info,
  MoreVert,
  Add,
  Delete,
  AttachFile,
  TextFields,
  Description,
  WarningAmberOutlined,
  HourglassEmptyOutlined,
  CheckCircleOutline,
  CancelOutlined,
} from '@mui/icons-material';
import { EnhancedTable, PageHeader, StatCard } from '../components/common';
import { usePermission } from '../hooks';
import { serviceApplicationsApi } from '../api/serviceApplications';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { colors } from '../theme/theme';
import { ServiceApplication } from '../types/serviceApplication';

// Fix Leaflet default marker icons
const DefaultIcon = L.icon({ iconUrl: icon, iconRetinaUrl: iconRetina, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

export const ServiceApplicationsPage = () => {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState<ServiceApplication | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [requestInfoDialogOpen, setRequestInfoDialogOpen] = useState(false);
  const [siteVisitDialogOpen, setSiteVisitDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [infoComment, setInfoComment] = useState('');
  const [infoRequirements, setInfoRequirements] = useState<Array<{ type: 'file' | 'text'; label: string }>>([]);
  const [siteVisitDate, setSiteVisitDate] = useState('');
  const [siteVisitNotes, setSiteVisitNotes] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuApp, setMenuApp] = useState<ServiceApplication | null>(null);
  const [approvalItems, setApprovalItems] = useState<any[]>([{
    service_id: '',
    zone_id: '',
    units: 1,
    description: '',
  }]);
  const [reviewData, setReviewData] = useState<any>(null);
  const [loadingReviewData, setLoadingReviewData] = useState(false);
  const [loadingViewDetails, setLoadingViewDetails] = useState(false);

  // Fetch applications
  const { data: applicationsData, isLoading } = useQuery({
    queryKey: ['serviceApplications', page, rowsPerPage, search, statusFilter],
    queryFn: () =>
      serviceApplicationsApi.getApplications({
        page: page + 1,
        per_page: rowsPerPage,
        search,
        status: statusFilter || undefined,
      }),
  });

  // Fetch statistics
  const { data: statistics } = useQuery({
    queryKey: ['applicationStats'],
    queryFn: () => serviceApplicationsApi.getStatistics(),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      serviceApplicationsApi.approve(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceApplications'] });
      queryClient.invalidateQueries({ queryKey: ['applicationStats'] });
      setApproveDialogOpen(false);
      setSelectedApp(null);
      showSnackbar('Application approved successfully!', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to approve application';
      showSnackbar(message, 'error');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { rejection_reason: string } }) =>
      serviceApplicationsApi.reject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceApplications'] });
      queryClient.invalidateQueries({ queryKey: ['applicationStats'] });
      setRejectDialogOpen(false);
      setSelectedApp(null);
      setRejectionReason('');
      showSnackbar('Application rejected successfully', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to reject application';
      showSnackbar(message, 'error');
    },
  });

  // Attach service mutation
  const startReviewMutation = useMutation({
    mutationFn: (id: number) => serviceApplicationsApi.startReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceApplications'] });
      queryClient.invalidateQueries({ queryKey: ['applicationStats'] });
      showSnackbar('Review started successfully!', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to start review';
      showSnackbar(message, 'error');
    },
  });

  const requestInfoMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      serviceApplicationsApi.requestInfo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceApplications'] });
      queryClient.invalidateQueries({ queryKey: ['applicationStats'] });
      setRequestInfoDialogOpen(false);
      setSelectedApp(null);
      setInfoComment('');
      setInfoRequirements([]);
      showSnackbar('Information requested successfully!', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to request information';
      showSnackbar(message, 'error');
    },
  });

  const siteVisitMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      serviceApplicationsApi.scheduleSiteVisit(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceApplications'] });
      queryClient.invalidateQueries({ queryKey: ['applicationStats'] });
      setSiteVisitDialogOpen(false);
      setSelectedApp(null);
      setSiteVisitDate('');
      setSiteVisitNotes('');
      showSnackbar('Site visit scheduled successfully!', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to schedule site visit';
      showSnackbar(message, 'error');
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: () =>
      serviceApplicationsApi.exportApplications({
        search,
        status: statusFilter || undefined,
      }),
    onSuccess: (data) => {
      // Create blob with proper CSV type
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `service_applications_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Export completed successfully', 'success');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to export applications';
      showSnackbar(message, 'error');
    },
  });

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: colors.warning.main,
      submitted: colors.warning.main,
      under_review: colors.info.main,
      approved: colors.success.main,
      rejected: colors.error.main,
      pending_site_visit: colors.info.main,
      additional_info_required: colors.warning.main,
    };
    return statusColors[status] || colors.grey[500];
  };

  const columns = [
    {
      id: 'submission_reference',
      label: 'Reference',
      render: (row: ServiceApplication) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary.main }}>
          {row.submission_reference}
        </Typography>
      ),
    },
    {
      id: 'user',
      label: 'Applicant',
      render: (row: ServiceApplication) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.user.first_name} {row.user.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.user.email}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'asset',
      label: 'Business / Asset',
      render: (row: ServiceApplication) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.asset?.asset_name || row.asset?.name || '—'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: ServiceApplication) => (
        <Chip
          label={row.status.replace(/_/g, ' ')}
          size="small"
          sx={{
            bgcolor: `${getStatusColor(row.status)}14`,
            color: getStatusColor(row.status),
            fontWeight: 600,
            textTransform: 'capitalize',
          }}
        />
      ),
    },
    {
      id: 'submitted_at',
      label: 'Submitted',
      render: (row: ServiceApplication) =>
        new Date(row.submitted_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (row: ServiceApplication) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, row)}
          sx={{ color: colors.grey[600] }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const handleViewApplication = async (app: ServiceApplication) => {
    setViewDialogOpen(true);
    setLoadingViewDetails(true);
    setSelectedApp(null);

    try {
      const fullApp = await serviceApplicationsApi.getApplication(app.id);
      if (fullApp) {
        setSelectedApp(fullApp);
      } else {
        throw new Error('Application not found');
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load application details';
      console.error('Error loading application details:', error);
      showSnackbar(message, 'error');
      setViewDialogOpen(false);
    } finally {
      setLoadingViewDetails(false);
    }
  };

  const handleOpenApproveDialog = async (app: ServiceApplication) => {
    setSelectedApp(app);
    setApproveDialogOpen(true);
    setLoadingReviewData(true);
    setReviewData(null);

    // Fetch review data (services and zones)
    try {
      const data = await serviceApplicationsApi.getReviewData(app.id);
      setReviewData(data);

      // Pre-populate with application's service/zone if available
      if (app.service_id && app.zone_id) {
        setApprovalItems([{
          service_id: app.service_id,
          zone_id: app.zone_id,
          units: 1,
          description: '',
        }]);
      } else {
        setApprovalItems([{
          service_id: '',
          zone_id: '',
          units: 1,
          description: '',
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch review data:', error);
      showSnackbar('Failed to load approval form. Please try again.', 'error');
      setApproveDialogOpen(false);
    } finally {
      setLoadingReviewData(false);
    }
  };

  const handleAddItem = () => {
    setApprovalItems([...approvalItems, {
      service_id: '',
      zone_id: '',
      units: 1,
      description: '',
    }]);
  };

  const handleRemoveItem = (index: number) => {
    if (approvalItems.length > 1) {
      setApprovalItems(approvalItems.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...approvalItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // If service changed, reset zone
    if (field === 'service_id') {
      newItems[index].zone_id = '';
    }

    setApprovalItems(newItems);
  };

  // Get zones that have charges for a specific service
  const getZonesForService = (serviceId: number) => {
    if (!reviewData || !serviceId) return [];

    const service = reviewData.services?.find((s: any) => s.id === serviceId);
    if (!service || !service.charges) return [];

    const zoneIdsWithCharges = service.charges.map((c: any) => c.zone_id);
    return reviewData.zones?.filter((z: any) => zoneIdsWithCharges.includes(z.id)) || [];
  };

  // Get charge amount for a service-zone combination
  const getChargeAmount = (serviceId: number, zoneId: number) => {
    if (!reviewData || !serviceId || !zoneId) return null;

    const service = reviewData.services?.find((s: any) => s.id === serviceId);
    if (!service || !service.charges) return null;

    const charge = service.charges.find((c: any) => c.zone_id === zoneId);
    return charge ? parseFloat(charge.amount) : null;
  };

  // Calculate item amount
  const calculateItemAmount = (item: any) => {
    const chargeAmount = getChargeAmount(item.service_id, item.zone_id);
    if (!chargeAmount) return 0;
    return chargeAmount * (item.units || 1);
  };

  // Calculate total amount
  const calculateTotalAmount = () => {
    return approvalItems.reduce((total, item) => total + calculateItemAmount(item), 0);
  };

  const handleApprove = () => {
    if (!selectedApp) return;

    // Validate all items
    const isValid = approvalItems.every(item =>
      item.service_id && item.zone_id && item.units > 0
    );

    if (!isValid) {
      showSnackbar('Please fill in all required fields (Service, Zone, and Units) for each item', 'warning');
      return;
    }

    approveMutation.mutate({
      id: selectedApp.id,
      data: { items: approvalItems },
    });
  };

  const handleReject = () => {
    if (!selectedApp || !rejectionReason.trim()) {
      return;
    }

    rejectMutation.mutate({
      id: selectedApp.id,
      data: { rejection_reason: rejectionReason },
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, app: ServiceApplication) => {
    setMenuAnchor(event.currentTarget);
    setMenuApp(app);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuApp(null);
  };

  const handleStartReview = (app: ServiceApplication) => {
    handleMenuClose();
    startReviewMutation.mutate(app.id);
  };

  const handleRequestInfo = () => {
    if (!selectedApp || !infoComment.trim()) {
      showSnackbar('Please provide instructions for the applicant', 'error');
      return;
    }

    requestInfoMutation.mutate({
      id: selectedApp.id,
      data: {
        comment: infoComment,
        requirements: infoRequirements.length > 0 ? infoRequirements : undefined,
      },
    });
  };

  const handleAddRequirement = () => {
    setInfoRequirements([...infoRequirements, { type: 'file', label: '' }]);
  };

  const handleRemoveRequirement = (index: number) => {
    setInfoRequirements(infoRequirements.filter((_, i) => i !== index));
  };

  const handleRequirementChange = (index: number, field: 'type' | 'label', value: string) => {
    const newRequirements = [...infoRequirements];
    newRequirements[index] = { ...newRequirements[index], [field]: value };
    setInfoRequirements(newRequirements);
  };

  const handleScheduleSiteVisit = () => {
    if (!selectedApp || !siteVisitDate) {
      showSnackbar('Please select a date and time', 'error');
      return;
    }

    siteVisitMutation.mutate({
      id: selectedApp.id,
      data: {
        scheduled_at: siteVisitDate,
        notes: siteVisitNotes,
      },
    });
  };


  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Service Applications"
          subtitle="Manage and process service applications"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Service Applications' },
          ]}
        />

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Total"
              value={statistics?.total || 0}
              icon={<Description />}
              color={colors.primary.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Pending"
              value={statistics?.pending || 0}
              icon={<HourglassEmptyOutlined />}
              color={colors.warning.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Under Review"
              value={statistics?.under_review || 0}
              icon={<HourglassEmptyOutlined />}
              color={colors.info.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Approved"
              value={statistics?.approved || 0}
              icon={<CheckCircleOutline />}
              color={colors.success.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <StatCard
              title="Rejected"
              value={statistics?.rejected || 0}
              icon={<CancelOutlined />}
              color={colors.error.main}
            />
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by reference, name, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="submitted">Submitted</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="resubmitted">Resubmitted</MenuItem>
                    <MenuItem value="additional_info_required">Additional Info Required</MenuItem>
                    <MenuItem value="pending_site_visit">Pending Site Visit</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 5 }} sx={{ textAlign: 'right' }}>
                {can('applications.export') && <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => exportMutation.mutate()}
                  disabled={exportMutation.isPending}
                >
                  Export
                </Button>}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <EnhancedTable
          columns={columns}
          rows={applicationsData?.data || []}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={applicationsData?.total || 0}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage="No service applications found"
        />

        {/* View Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: colors.primary.main, color: 'white', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility />
              <Typography variant="h6" component="span">
                Application Details
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {loadingViewDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : selectedApp ? (
              <Box>
                {/* Application Info */}
                <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.grey[50], border: `1px solid ${colors.grey[200]}` }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.primary.main }}>
                    Application Information
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Reference Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {selectedApp.submission_reference}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedApp.status.replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            bgcolor: `${getStatusColor(selectedApp.status)}14`,
                            color: getStatusColor(selectedApp.status),
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Submitted At
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {new Date(selectedApp.submitted_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Grid>
                    {selectedApp.approved_at && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Approved At
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, color: colors.success.main }}>
                          {new Date(selectedApp.approved_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Grid>
                    )}
                    {selectedApp.rejected_at && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Rejected At
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, color: colors.error.main }}>
                          {new Date(selectedApp.rejected_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>

                {/* Applicant Info */}
                <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.grey[50], border: `1px solid ${colors.grey[200]}` }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.primary.main }}>
                    Applicant Information
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Full Name
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {selectedApp.user.first_name} {selectedApp.user.last_name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Email Address
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {selectedApp.user.email}
                      </Typography>
                    </Grid>
                    {selectedApp.user.profile?.phone && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Phone Number
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {selectedApp.user.profile.phone}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>

                {/* Services */}
                {(selectedApp.services && selectedApp.services.length > 0) || selectedApp.service ? (
                  <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.grey[50], border: `1px solid ${colors.grey[200]}` }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.primary.main }}>
                      Requested Services
                    </Typography>
                    <Grid container spacing={1.5}>
                      {selectedApp.services && selectedApp.services.length > 0 ? (
                        selectedApp.services.map((service, index) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={service.id}>
                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: 'white',
                                border: `1px solid ${colors.primary.light}`,
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {index + 1}. {service.name}
                              </Typography>
                            </Box>
                          </Grid>
                        ))
                      ) : selectedApp.service ? (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: 'white',
                              border: `1px solid ${colors.primary.light}`,
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selectedApp.service.name}
                            </Typography>
                            {selectedApp.service.category && (
                              <Typography variant="caption" color="text.secondary">
                                {selectedApp.service.category.name}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      ) : null}
                      {selectedApp.zone && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Zone
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {selectedApp.zone.name}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                ) : null}

                {/* Asset Information */}
                {selectedApp.asset && (
                  <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.grey[50], border: `1px solid ${colors.grey[200]}` }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.primary.main }}>
                      Business / Asset Information
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Business Name
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                          {selectedApp.asset.asset_name || selectedApp.asset.name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Reference
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                          {selectedApp.asset.asset_reference || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Status
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, textTransform: 'capitalize' }}>
                          {selectedApp.asset.status || 'N/A'}
                        </Typography>
                      </Grid>
                      {selectedApp.asset.business_type && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Business Type
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {typeof selectedApp.asset.business_type === 'object' ? selectedApp.asset.business_type?.name : selectedApp.asset.business_type}
                          </Typography>
                        </Grid>
                      )}
                      {selectedApp.asset.business_category && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Business Category
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {typeof selectedApp.asset.business_category === 'object' ? selectedApp.asset.business_category?.name : selectedApp.asset.business_category}
                          </Typography>
                        </Grid>
                      )}
                      {selectedApp.asset.owner_details && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Owner
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {selectedApp.asset.owner_details.name || 'N/A'}
                          </Typography>
                        </Grid>
                      )}
                      {selectedApp.asset.location && typeof selectedApp.asset.location === 'object' && selectedApp.asset.location.address && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Address
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {selectedApp.asset.location.address}
                          </Typography>
                        </Grid>
                      )}
                      {/* Map */}
                      {(() => {
                        const loc = selectedApp.asset.location;
                        const lat = typeof loc === 'object' ? loc?.latitude : null;
                        const lng = typeof loc === 'object' ? loc?.longitude : null;
                        if (lat && lng) {
                          return (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Business Location
                              </Typography>
                              <Box sx={{ height: 250, borderRadius: 2, overflow: 'hidden', border: `1px solid ${colors.grey[300]}` }}>
                                <MapContainer
                                  center={[lat, lng]}
                                  zoom={15}
                                  style={{ height: '100%', width: '100%' }}
                                  scrollWheelZoom={false}
                                >
                                  <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                  />
                                  <Marker position={[lat, lng]}>
                                    <Popup>
                                      <strong>{selectedApp.asset.asset_name || selectedApp.asset.name}</strong>
                                      <br />
                                      {lat.toFixed(6)}, {lng.toFixed(6)}
                                    </Popup>
                                  </Marker>
                                </MapContainer>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace', display: 'block' }}>
                                Coordinates: {lat.toFixed(6)}, {lng.toFixed(6)}
                              </Typography>
                            </Grid>
                          );
                        }
                        return null;
                      })()}
                    </Grid>
                  </Card>
                )}

                {/* Site Visit Information */}
                {selectedApp.site_visit_scheduled_at && (
                  <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.info.light + '10', border: `1px solid ${colors.info.main}` }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.info.main }}>
                      Site Visit Information
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Scheduled Date & Time
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                          {new Date(selectedApp.site_visit_scheduled_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Grid>
                      {selectedApp.site_visit_notes && (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Notes
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {selectedApp.site_visit_notes}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                )}

                {/* Rejection Information */}
                {selectedApp.rejected_at && selectedApp.rejection_reason && (
                  <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.error.light + '10', border: `1px solid ${colors.error.main}` }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.error.main }}>
                      Rejection Information
                    </Typography>
                    <Typography variant="body1">{selectedApp.rejection_reason}</Typography>
                  </Card>
                )}

                {/* Bills */}
                {selectedApp.bills && selectedApp.bills.length > 0 && (
                  <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.success.light + '10', border: `1px solid ${colors.success.main}` }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: colors.success.main }}>
                      Generated Bills
                    </Typography>
                    {selectedApp.bills.map((bill) => (
                      <Card key={bill.id} sx={{ mb: 2, p: 2, bgcolor: 'white', border: `1px solid ${colors.grey[300]}` }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Invoice Number
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                              {bill.invoice_number}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Total Amount
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: colors.success.main }}>
                              KES {bill.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Payment Status
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={bill.is_paid ? 'Paid' : 'Unpaid'}
                                size="small"
                                color={bill.is_paid ? 'success' : 'warning'}
                              />
                            </Box>
                          </Grid>

                          {/* Bill Items */}
                          {bill.items && bill.items.length > 0 && (
                            <Grid size={{ xs: 12 }}>
                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                                Bill Items
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {bill.items.map((item) => (
                                  <Box
                                    key={item.id}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: colors.grey[50],
                                      borderRadius: 1,
                                      border: `1px solid ${colors.grey[200]}`,
                                    }}
                                  >
                                    <Grid container spacing={1} alignItems="center">
                                      <Grid size={{ xs: 12, sm: 4 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {item.service.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Zone: {item.zone.name}
                                        </Typography>
                                      </Grid>
                                      <Grid size={{ xs: 6, sm: 3 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Units
                                        </Typography>
                                        <Typography variant="body2">{item.units}</Typography>
                                      </Grid>
                                      <Grid size={{ xs: 6, sm: 3 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Unit Price
                                        </Typography>
                                        <Typography variant="body2">
                                          KES {item.unit_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </Typography>
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 2 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Amount
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary.main }}>
                                          KES {item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                ))}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    ))}
                  </Card>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No application selected</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: colors.grey[50] }}>
            <Button onClick={() => setViewDialogOpen(false)} variant="outlined" size="large">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog
          open={approveDialogOpen}
          onClose={() => setApproveDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{
            bgcolor: colors.success.main,
            color: 'white',
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CheckCircle />
            Approve Application & Generate Bill
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {loadingReviewData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : selectedApp && reviewData ? (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Approving this application will generate a bill with the items specified below.
                </Alert>

                {/* Application Info */}
                <Card sx={{ mb: 3, p: 2.5, bgcolor: colors.grey[50], border: `1px solid ${colors.grey[200]}` }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: colors.grey[700] }}>
                    Application Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Reference Number
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary.main }}>
                        {selectedApp.submission_reference}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Applicant Name
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedApp.user.first_name} {selectedApp.user.last_name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Email Address
                      </Typography>
                      <Typography variant="body2">
                        {selectedApp.user.email}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Phone Number
                      </Typography>
                      <Typography variant="body2">
                        {selectedApp.user.profile?.phone || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>

                {/* Asset / Business Info */}
                {selectedApp.asset && (
                  <Card sx={{ mb: 3, p: 2.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#92400e' }}>
                      Business / Asset Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Business Name
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedApp.asset.asset_name || selectedApp.asset.name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Reference
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {selectedApp.asset.asset_reference || 'N/A'}
                        </Typography>
                      </Grid>
                      {selectedApp.asset.business_type && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Business Type
                          </Typography>
                          <Typography variant="body2">
                            {typeof selectedApp.asset.business_type === 'object' ? selectedApp.asset.business_type?.name : selectedApp.asset.business_type}
                          </Typography>
                        </Grid>
                      )}
                      {selectedApp.asset.business_category && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Category
                          </Typography>
                          <Typography variant="body2">
                            {typeof selectedApp.asset.business_category === 'object' ? selectedApp.asset.business_category?.name : selectedApp.asset.business_category}
                          </Typography>
                        </Grid>
                      )}
                      {selectedApp.asset.owner_details?.name && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Owner
                          </Typography>
                          <Typography variant="body2">
                            {selectedApp.asset.owner_details.name}
                          </Typography>
                        </Grid>
                      )}
                      {selectedApp.asset.status && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Status
                          </Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {selectedApp.asset.status}
                          </Typography>
                        </Grid>
                      )}
                      {/* Map in approve dialog */}
                      {(() => {
                        const loc = selectedApp.asset.location;
                        const lat = typeof loc === 'object' ? (loc as any)?.latitude : null;
                        const lng = typeof loc === 'object' ? (loc as any)?.longitude : null;
                        if (lat && lng) {
                          return (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Business Location
                              </Typography>
                              <Box sx={{ height: 200, borderRadius: 2, overflow: 'hidden', border: '1px solid #fde68a' }}>
                                <MapContainer
                                  center={[lat, lng]}
                                  zoom={15}
                                  style={{ height: '100%', width: '100%' }}
                                  scrollWheelZoom={false}
                                >
                                  <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                  />
                                  <Marker position={[lat, lng]}>
                                    <Popup>{selectedApp.asset.asset_name || selectedApp.asset.name}</Popup>
                                  </Marker>
                                </MapContainer>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace', display: 'block' }}>
                                {lat.toFixed(6)}, {lng.toFixed(6)}
                              </Typography>
                            </Grid>
                          );
                        }
                        return null;
                      })()}
                    </Grid>
                  </Card>
                )}

                {/* Billing Items */}
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Billing Items
                </Typography>

                {approvalItems.map((item, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2.5,
                      bgcolor: 'white',
                      border: `2px solid ${colors.grey[200]}`,
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: colors.primary.main,
                        boxShadow: `0 0 0 1px ${colors.primary.main}40`
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Item #{index + 1}
                      </Typography>
                      {approvalItems.length > 1 && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(index)}
                          startIcon={<Cancel />}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Service *</InputLabel>
                          <Select
                            value={item.service_id}
                            onChange={(e) => handleItemChange(index, 'service_id', e.target.value)}
                            label="Service *"
                          >
                            {reviewData.services
                              ?.filter((service: any) => {
                                // Exclude services already selected in other items
                                const selectedByOthers = approvalItems
                                  .filter((_: any, i: number) => i !== index)
                                  .map((ai: any) => ai.service_id);
                                return !selectedByOthers.includes(service.id);
                              })
                              .map((service: any) => (
                              <MenuItem key={service.id} value={service.id}>
                                {service.name} ({service.charges?.length || 0} zone{service.charges?.length !== 1 ? 's' : ''})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Zone *</InputLabel>
                          <Select
                            value={item.zone_id}
                            onChange={(e) => handleItemChange(index, 'zone_id', e.target.value)}
                            label="Zone *"
                            disabled={!item.service_id}
                          >
                            {item.service_id ? (
                              getZonesForService(item.service_id).length > 0 ? (
                                getZonesForService(item.service_id).map((zone: any) => {
                                  const charge = getChargeAmount(item.service_id, zone.id);
                                  return (
                                    <MenuItem key={zone.id} value={zone.id}>
                                      {zone.name} {charge ? `(KES ${charge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}
                                    </MenuItem>
                                  );
                                })
                              ) : (
                                <MenuItem value="" disabled>
                                  No zones with charges for this service
                                </MenuItem>
                              )
                            ) : (
                              <MenuItem value="" disabled>
                                Select service first
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Units *"
                          required
                          value={item.units}
                          onChange={(e) => handleItemChange(index, 'units', parseInt(e.target.value) || 1)}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description (Optional)"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Additional details about this item"
                        />
                      </Grid>
                      {item.service_id && item.zone_id && (
                        <Grid size={{ xs: 12 }}>
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              bgcolor: colors.grey[50],
                              borderRadius: 1,
                              border: `1px solid ${colors.grey[200]}`
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              <strong>Item Amount:</strong> KES {calculateItemAmount(item).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {item.units > 1 && (
                                <span style={{ marginLeft: 8, fontSize: '0.85em' }}>
                                  ({item.units} × KES {(getChargeAmount(item.service_id, item.zone_id) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                </span>
                              )}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                ))}

                {/* Total Amount */}
                <Card
                  sx={{
                    mt: 2,
                    p: 2.5,
                    bgcolor: colors.primary.light + '10',
                    border: `2px solid ${colors.primary.main}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      Total Bill Amount
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      KES {calculateTotalAmount().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Card>

                <Button
                  variant="outlined"
                  onClick={handleAddItem}
                  disabled={approvalItems.length >= (reviewData.services?.length || 0)}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  + Add Another Item
                </Button>
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="error">Failed to load approval form</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: colors.grey[50] }}>
            <Button onClick={() => setApproveDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={approveMutation.isPending || loadingReviewData}
              startIcon={approveMutation.isPending ? <CircularProgress size={20} /> : <CheckCircle />}
              size="large"
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve & Generate Bill'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject Application</DialogTitle>
          <DialogContent>
            {selectedApp && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Reference:</strong> {selectedApp.submission_reference}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this application..."
                  required
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={rejectMutation.isPending || !rejectionReason.trim()}
            >
              {rejectMutation.isPending ? <CircularProgress size={24} /> : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Request Info Dialog */}
        <Dialog
          open={requestInfoDialogOpen}
          onClose={() => setRequestInfoDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: colors.warning.main, color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info />
              Request Additional Information
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedApp && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Reference:</strong> {selectedApp.submission_reference}
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Specify what additional information or documents you need from the applicant.
                </Alert>

                {/* Requirements List */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Required Items
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Add specific documents or information needed
                  </Typography>

                  {infoRequirements.map((req, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={req.type}
                          onChange={(e) => handleRequirementChange(index, 'type', e.target.value)}
                          startAdornment={
                            req.type === 'file' ? (
                              <AttachFile sx={{ fontSize: 18, mr: 0.5, color: colors.grey[600] }} />
                            ) : (
                              <TextFields sx={{ fontSize: 18, mr: 0.5, color: colors.grey[600] }} />
                            )
                          }
                        >
                          <MenuItem value="file">File Upload</MenuItem>
                          <MenuItem value="text">Text Input</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Label (e.g., ID Copy, Business License)"
                        value={req.label}
                        onChange={(e) => handleRequirementChange(index, 'label', e.target.value)}
                        required
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRequirement(index)}
                        sx={{ color: colors.error.main }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddRequirement}
                    startIcon={<Add />}
                    sx={{ mt: 1 }}
                  >
                    Add Requirement
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Instructions *"
                  required
                  value={infoComment}
                  onChange={(e) => setInfoComment(e.target.value)}
                  placeholder="Provide details about what you need from the applicant..."
                  helperText="Minimum 10 characters required"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setRequestInfoDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleRequestInfo}
              disabled={requestInfoMutation.isPending || !infoComment.trim() || infoComment.length < 10}
              startIcon={requestInfoMutation.isPending && <CircularProgress size={20} />}
            >
              {requestInfoMutation.isPending ? 'Sending...' : 'Send Request'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Schedule Site Visit Dialog */}
        <Dialog
          open={siteVisitDialogOpen}
          onClose={() => setSiteVisitDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: colors.warning.main, color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventAvailable />
              Schedule Site Visit
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedApp && (
              <Box>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  <strong>Reference:</strong> {selectedApp.submission_reference}
                </Typography>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Date & Time *"
                  required
                  value={siteVisitDate}
                  onChange={(e) => setSiteVisitDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={siteVisitNotes}
                  onChange={(e) => setSiteVisitNotes(e.target.value)}
                  placeholder="Instructions for the visit..."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setSiteVisitDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleScheduleSiteVisit}
              disabled={siteVisitMutation.isPending || !siteVisitDate}
              startIcon={siteVisitMutation.isPending && <CircularProgress size={20} />}
            >
              {siteVisitMutation.isPending ? 'Scheduling...' : 'Schedule Visit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 250,
              mt: 1,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
              },
            },
          }}
        >
          {menuApp && [
              <MenuItem key="view" onClick={() => { handleMenuClose(); handleViewApplication(menuApp); }}>
                <ListItemIcon>
                  <Visibility fontSize="small" sx={{ color: colors.primary.main }} />
                </ListItemIcon>
                <ListItemText
                  primary="View Details"
                  secondary="See full application information"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>,

              <Divider key="div-1" sx={{ my: 1 }} />,

              ['pending', 'submitted'].includes(menuApp.status) && (
                <MenuItem key="start-review" onClick={() => handleStartReview(menuApp)}>
                  <ListItemIcon>
                    <PlayCircleOutline fontSize="small" sx={{ color: colors.info.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Start Review"
                    secondary="Begin reviewing this application"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </MenuItem>
              ),

              ['under_review', 'resubmitted', 'additional_info_required'].includes(menuApp.status) && (
                <MenuItem key="request-info" onClick={() => { handleMenuClose(); setSelectedApp(menuApp); setRequestInfoDialogOpen(true); }}>
                  <ListItemIcon>
                    <Info fontSize="small" sx={{ color: colors.warning.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Request Information"
                    secondary="Ask applicant for more details"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </MenuItem>
              ),

              ['under_review', 'resubmitted', 'additional_info_required'].includes(menuApp.status) && (
                <MenuItem key="site-visit" onClick={() => { handleMenuClose(); setSelectedApp(menuApp); setSiteVisitDialogOpen(true); }}>
                  <ListItemIcon>
                    <EventAvailable fontSize="small" sx={{ color: colors.warning.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Schedule Site Visit"
                    secondary="Arrange an on-site inspection"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </MenuItem>
              ),

              ['pending', 'submitted', 'under_review', 'resubmitted', 'pending_site_visit', 'additional_info_required'].includes(menuApp.status) && (
                <Divider key="div-2" sx={{ my: 1 }} />
              ),

              can('applications.approve') && ['pending', 'submitted', 'under_review', 'resubmitted', 'pending_site_visit', 'additional_info_required'].includes(menuApp.status) && (
                <MenuItem
                  key="approve"
                  onClick={() => { handleMenuClose(); handleOpenApproveDialog(menuApp); }}
                  sx={{
                    bgcolor: colors.success.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: colors.success.dark,
                    },
                  }}
                >
                  <ListItemIcon>
                    <CheckCircle fontSize="small" sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Approve Application"
                    secondary="Approve and generate bill"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'white' }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255, 255, 255, 0.8)' }}
                  />
                </MenuItem>
              ),

              can('applications.reject') && ['pending', 'submitted', 'under_review', 'resubmitted', 'pending_site_visit', 'additional_info_required'].includes(menuApp.status) && (
                <MenuItem
                  key="reject"
                  onClick={() => { handleMenuClose(); setSelectedApp(menuApp); setRejectDialogOpen(true); }}
                  sx={{
                    bgcolor: colors.error.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: colors.error.dark,
                    },
                  }}
                >
                  <ListItemIcon>
                    <Cancel fontSize="small" sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Reject Application"
                    secondary="Decline with reason"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'white' }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255, 255, 255, 0.8)' }}
                  />
                </MenuItem>
              ),
          ]}
        </Menu>
      </Box>
    </AdminLayout>
  );
};
